import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Controller, useForm, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Save, Trash2 } from 'lucide-react';
import {
  Button,
  Checkbox,
  DateField,
  ErrorState,
  FormActions,
  FormField,
  FormGrid,
  Loading,
  NumberField,
  PageHeader,
  Panel,
  Select,
  SubTableEditor,
  TextField,
  Textarea,
  useConfirm,
  useToast,
} from '@/components';
import { useCurrentUser } from '@/auth/AuthProvider';
import { READ_ONLY_ROLES } from '@/auth/types';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { ApiError, errorMessage } from '@/lib/api';
import { useCodeValues } from '@/features/masters/api';
import { BusinessOperatorSelect } from '@/features/masters/components/BusinessOperatorSelect';
import { useCreateVessel, useDeleteVessel, useUpdateVessel, useVessel, type SaveResult, type VesselDetail } from './api';
import { emptyCapacity, emptyPerson, emptyVesselForm, toBody, toForm, vesselFormSchema, type VesselFormValues } from './schema';
import './vessels.css';

/** S-11 参加船名簿 登録・編集（/vessels/new, /vessels/:id/edit） */
export function VesselFormPage() {
  const { id } = useParams();
  const user = useCurrentUser();
  const vesselId = id ? Number(id) : null;
  const { data, isPending, error, refetch } = useVessel(vesselId);
  const codes = useCodeValues();
  if (READ_ONLY_ROLES.includes(user.organizationType)) {
    return <ErrorState error={new ApiError(403, 'FORBIDDEN', 'この画面を操作する権限がありません。')} />;
  }
  if (vesselId != null) {
    if (isPending) return <Loading block />;
    if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  }
  // コード値（選択肢）が揃う前に描画すると、保存済みの選択値が select に反映されない
  if (codes.isPending) return <Loading block />;
  return <VesselForm key={vesselId ?? 'new'} vessel={vesselId != null ? data : undefined} />;
}

/** API の指摘（details の path）を RHF のフィールドへ反映。漁業種類は配列要素名に変換する */
function applyServerErrors(e: unknown, setError: (name: never, err: { type: string; message: string }) => void): boolean {
  if (!(e instanceof ApiError)) return false;
  const entries = Object.entries(e.fieldErrors);
  for (const [path, message] of entries) {
    const name = path.replace(/^fishingMethods\.(\d+)$/, 'fishingMethods.$1.value');
    setError(name as never, { type: 'server', message });
  }
  return entries.length > 0;
}

const rootMessage = (err: FieldErrors<VesselFormValues>[keyof VesselFormValues]): string | undefined => {
  const x = err as { message?: string; root?: { message?: string } } | undefined;
  return x?.message ?? x?.root?.message;
};

function VesselForm({ vessel }: { vessel?: VesselDetail }) {
  const isNew = !vessel;
  const user = useCurrentUser();
  const isAdmin = user.organizationType === '基金';
  const navigate = useNavigate();
  const { year, yearPath } = useFiscalYear();
  const toast = useToast();
  const confirm = useConfirm();
  const { data: codes } = useCodeValues();
  const create = useCreateVessel();
  const update = useUpdateVessel(vessel?.id ?? 0);
  const del = useDeleteVessel();

  const ownId = user.organizationType === '漁協' && user.businessOperatorId != null ? Number(user.businessOperatorId) : null;
  const defaults = useMemo(() => (vessel ? toForm(vessel) : emptyVesselForm(ownId)), [vessel, ownId]);

  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<VesselFormValues>({ resolver: zodResolver(vesselFormSchema), defaultValues: defaults });

  // 漁協は kintone と同じく、既存船の基本情報（船名・登録番号・トン数・電話）と承認期間を変更できない
  const lockCore = !isAdmin && !isNew;
  const lockApproval = !isAdmin;
  const listPath = yearPath('vessels');
  const approvedRow = (index: number, list: 'captains' | 'crewMembers' | 'capacities') => {
    const row = vessel?.[list][index];
    return !isAdmin && !!row && !!row.approvalFrom && !!row.approvalTo;
  };
  const fishingLocked = !isAdmin && !!vessel?.isApproved;

  const onSubmit = handleSubmit(
    async (values) => {
      try {
        const body = toBody(values, year, isAdmin);
        const res: SaveResult = isNew ? await create.mutateAsync(body) : await update.mutateAsync(body);
        toast.success(isNew ? '参加船を登録しました' : '参加船を更新しました', `${values.name}（参加船ID ${res.vesselNo}）`);
        if (res.warnings.length) toast.warning('確認してください', res.warnings.map((w) => w.message).join('\n'));
        navigate(yearPath(`vessels/${res.id}`));
      } catch (e) {
        if (!applyServerErrors(e, setError as never)) toast.error('保存できませんでした', errorMessage(e));
        else toast.error('保存できませんでした', '入力内容に誤りがあります。赤字の項目を確認してください。');
      }
    },
    () => toast.error('保存できませんでした', '入力内容に誤りがあります。赤字の項目を確認してください。'),
  );

  const onDelete = async () => {
    if (!vessel) return;
    const ok = await confirm({
      title: '参加船を削除しますか？',
      message: `${vessel.name}（${vessel.registrationNo}）を削除します。\n事前連絡・速報などで使用されている場合は削除できません。この操作は取り消せません。`,
      tone: 'danger',
      confirmLabel: '削除する',
    });
    if (!ok) return;
    try {
      await del.mutateAsync(vessel.id);
      toast.success('参加船を削除しました', vessel.name);
      navigate(listPath);
    } catch (e) {
      toast.error('削除できませんでした', errorMessage(e));
    }
  };

  const onCancel = async () => {
    if (isDirty && !(await confirm({ title: '入力内容を破棄しますか？', message: '保存していない変更は失われます。', confirmLabel: '破棄して戻る', tone: 'danger' }))) return;
    navigate(vessel ? yearPath(`vessels/${vessel.id}`) : listPath);
  };

  const periodRow = (label: string, num: 'phone', hint?: string, lock = false) => (
    <>
      <FormField label={label} error={errors[num]?.message} hint={hint}>
        <TextField readOnly={lock} {...register(num)} />
      </FormField>
      <FormField label={`${label} 開始日`} error={errors[`${num}From`]?.message}>
        <DateField readOnly={lock} {...register(`${num}From`)} />
      </FormField>
      <FormField label={`${label} 終了日`} error={errors[`${num}To`]?.message}>
        <DateField readOnly={lock} {...register(`${num}To`)} />
      </FormField>
    </>
  );

  const personColumns = (list: 'captains' | 'crewMembers') => [
    {
      key: 'name',
      header: list === 'captains' ? '船長氏名' : '乗組員名',
      width: 170,
      required: list === 'captains',
      render: ({ path, index }: { path: `${typeof list}.${number}`; index: number }) => (
        <>
          <TextField readOnly={approvedRow(index, list)} aria-label={`${index + 1}行目の氏名`} invalid={!!errors[list]?.[index]?.name} {...register(`${path}.name`)} />
          {errors[list]?.[index]?.name?.message && <div className="bs-plan-error">{errors[list]?.[index]?.name?.message}</div>}
        </>
      ),
    },
    {
      key: 'approvalFrom',
      header: '承認期間 FROM',
      width: 150,
      render: ({ path }: { path: `${typeof list}.${number}` }) => <DateField readOnly={lockApproval} aria-label="承認期間FROM" {...register(`${path}.approvalFrom`)} />,
    },
    {
      key: 'approvalTo',
      header: '承認期間 TO',
      width: 150,
      render: ({ path, index }: { path: `${typeof list}.${number}`; index: number }) => (
        <>
          <DateField readOnly={lockApproval} aria-label="承認期間TO" invalid={!!errors[list]?.[index]?.approvalTo} {...register(`${path}.approvalTo`)} />
          {errors[list]?.[index]?.approvalTo?.message && <div className="bs-plan-error">{errors[list]?.[index]?.approvalTo?.message}</div>}
        </>
      ),
    },
  ];

  return (
    <form onSubmit={onSubmit} noValidate>
      <PageHeader
        eyebrow="船舶・計画 ／ 参加船名簿"
        title={isNew ? '参加船の新規登録' : '参加船の編集'}
        description={
          isNew
            ? `${year}年度の参加船を登録します。承認（参加期間の設定）は基金が一括承認で行います。`
            : `参加船ID ${vessel.vesselNo} ／ ${vessel.businessOperatorName}${lockCore ? '　※ 船名・漁船登録番号・トン数・電話・承認期間は基金のみ変更できます' : ''}`
        }
      />

      <Panel title="基本情報">
        <FormGrid columns={3}>
          <FormField label="事業実施者" required error={errors.businessOperatorId?.message}>
            <Controller
              control={control}
              name="businessOperatorId"
              render={({ field }) => (
                <BusinessOperatorSelect fiscalYear={year} value={field.value} onChange={field.onChange} placeholder="選択してください" disabled={!isNew} invalid={!!errors.businessOperatorId} />
              )}
            />
          </FormField>
          <FormField label="名簿No" hint={isAdmin ? '空欄の場合は自動採番' : '基金が設定します'}>
            <Controller control={control} name="displayOrder" render={({ field }) => <NumberField {...field} width="sm" disabled={!isAdmin} />} />
          </FormField>
          <FormField label="氏名又は名称（船主）" required error={errors.ownerName?.message}>
            <TextField readOnly={lockCore} {...register('ownerName')} />
          </FormField>
          <FormField label="船名" required error={errors.name?.message}>
            <TextField readOnly={lockCore} {...register('name')} />
          </FormField>
          <FormField label="船名（カナ）" required error={errors.nameKana?.message} hint="全角カタカナ">
            <TextField {...register('nameKana')} />
          </FormField>
          <FormField label="漁船登録番号" required error={errors.registrationNo?.message} hint="15桁以内。年度内で一意">
            <TextField readOnly={lockCore} {...register('registrationNo')} />
          </FormField>
          <FormField label="船舶トン数" required error={errors.tonnage?.message}>
            <Controller control={control} name="tonnage" render={({ field }) => <NumberField {...field} decimals={2} unit="トン" disabled={lockCore} invalid={!!errors.tonnage} />} />
          </FormField>
          <FormField label="馬力" error={errors.horsepower?.message}>
            <TextField width="sm" {...register('horsepower')} />
          </FormField>
          <FormField label="住所（船主）" error={errors.address?.message}>
            <TextField {...register('address')} />
          </FormField>
          <FormField label="燃油種類（既定値）">
            <Select placeholder="未設定" options={(codes?.fuelTypes ?? []).map((v) => ({ value: v, label: v }))} {...register('fuelType')} />
          </FormField>
          <FormField label="税算出（端数処理）" hint="賃金・用船料・燃油の消費税額に適用">
            <Select options={(codes?.taxRoundings ?? ['四捨五入', '切り捨て', '切り上げ']).map((v) => ({ value: v, label: v }))} {...register('taxRounding')} />
          </FormField>
          <FormField label="インボイス登録">
            <Controller control={control} name="isInvoiceRegistered" render={({ field }) => <Checkbox label="登録者（インボイス対象）" checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />} />
          </FormField>
          {isAdmin && (
            <FormField label="共済対象">
              <Controller control={control} name="isMutualAidTarget" render={({ field }) => <Checkbox label="共済対象" checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />} />
            </FormField>
          )}
        </FormGrid>
      </Panel>

      <Panel title="承認期間（参加期間）" description="承認済みの船だけが業務入力の選択肢に表示されます。基金が「一括承認」または本画面で設定します。">
        <FormGrid columns={3}>
          <FormField label="承認期間 FROM" error={errors.approvalFrom?.message}>
            <DateField showWareki readOnly={lockApproval} {...register('approvalFrom')} />
          </FormField>
          <FormField label="承認期間 TO" error={errors.approvalTo?.message}>
            <DateField showWareki readOnly={lockApproval} {...register('approvalTo')} />
          </FormField>
        </FormGrid>
      </Panel>

      <Panel title="船舶電話">
        <div className="bs-fieldgroup">
          {periodRow('船舶電話番号', 'phone', undefined, lockCore)}
        </div>
      </Panel>

      <Panel title="請求者情報" description="報告書兼請求書（別記3）・助成金領収書（別記4）に印字されます。">
        <FormGrid columns={3}>
          <FormField label="請求先宛先">
            <Select placeholder="未設定" options={(codes?.billingAddressees ?? []).map((v) => ({ value: v, label: v }))} {...register('billingAddressee')} />
          </FormField>
          <FormField label="請求者名（氏名）" error={errors.claimantName?.message}>
            <TextField {...register('claimantName')} />
          </FormField>
          <FormField label="請求者住所" error={errors.claimantAddress?.message}>
            <TextField {...register('claimantAddress')} />
          </FormField>
          <FormField label="請求者登録番号（T を除く13桁）" error={errors.claimantInvoiceNo?.message}>
            <TextField prefix="T" width="md" {...register('claimantInvoiceNo')} />
          </FormField>
          <FormField label="用船料受領者名" error={errors.charterFeeRecipient?.message}>
            <TextField {...register('charterFeeRecipient')} />
          </FormField>
        </FormGrid>
      </Panel>

      <Panel title="船長・乗組員・乗船人数・漁業種類" description="明細は保存時にすべて置き換えられます。承認済みの行は漁協では変更・削除できません。">
        <div style={{ display: 'grid', gap: 20 }}>
          <SubTableEditor
            control={control}
            name="captains"
            title="船長（1行以上）"
            minRows={1}
            createRow={emptyPerson}
            error={rootMessage(errors.captains)}
            columns={personColumns('captains')}
          />
          <SubTableEditor control={control} name="crewMembers" title="乗組員" createRow={emptyPerson} emptyMessage="乗組員は登録されていません" columns={personColumns('crewMembers')} />
          <SubTableEditor
            control={control}
            name="capacities"
            title="乗船人数（1行以上）"
            minRows={1}
            createRow={emptyCapacity}
            error={rootMessage(errors.capacities)}
            columns={[
              {
                key: 'boardingCount',
                header: '乗船人員',
                width: 120,
                required: true,
                render: ({ path, index }) => (
                  <>
                    <Controller control={control} name={`${path}.boardingCount`} render={({ field }) => <NumberField {...field} aria-label="乗船人員" unit="人" invalid={!!errors.capacities?.[index]?.boardingCount} />} />
                    {errors.capacities?.[index]?.boardingCount?.message && <div className="bs-plan-error">{errors.capacities[index]?.boardingCount?.message}</div>}
                  </>
                ),
              },
              {
                key: 'totalBoardingCount',
                header: '全乗船人員',
                width: 120,
                required: true,
                render: ({ path, index }) => (
                  <>
                    <Controller control={control} name={`${path}.totalBoardingCount`} render={({ field }) => <NumberField {...field} aria-label="全乗船人員" unit="人" invalid={!!errors.capacities?.[index]?.totalBoardingCount} />} />
                    {errors.capacities?.[index]?.totalBoardingCount?.message && <div className="bs-plan-error">{errors.capacities[index]?.totalBoardingCount?.message}</div>}
                  </>
                ),
              },
              { key: 'approvalFrom', header: '承認期間 FROM', width: 150, render: ({ path }) => <DateField readOnly={lockApproval} aria-label="承認期間FROM" {...register(`${path}.approvalFrom`)} /> },
              {
                key: 'approvalTo',
                header: '承認期間 TO',
                width: 150,
                render: ({ path, index }) => (
                  <>
                    <DateField readOnly={lockApproval} aria-label="承認期間TO" invalid={!!errors.capacities?.[index]?.approvalTo} {...register(`${path}.approvalTo`)} />
                    {errors.capacities?.[index]?.approvalTo?.message && <div className="bs-plan-error">{errors.capacities[index]?.approvalTo?.message}</div>}
                  </>
                ),
              },
            ]}
          />
          <SubTableEditor
            control={control}
            name="fishingMethods"
            title="漁業種類（1行以上）"
            minRows={1}
            readOnly={fishingLocked}
            createRow={() => ({ value: '' })}
            error={rootMessage(errors.fishingMethods)}
            columns={[
              {
                key: 'value',
                header: '漁業種類',
                width: 240,
                required: true,
                render: ({ path, index }) => (
                  <>
                    <Select
                      placeholder="選択してください"
                      aria-label={`${index + 1}行目の漁業種類`}
                      disabled={fishingLocked}
                      invalid={!!errors.fishingMethods?.[index]?.value}
                      options={(codes?.fishingMethods ?? []).map((v) => ({ value: v, label: v }))}
                      {...register(`${path}.value`)}
                    />
                    {errors.fishingMethods?.[index]?.value?.message && <div className="bs-plan-error">{errors.fishingMethods[index]?.value?.message}</div>}
                  </>
                ),
              },
            ]}
          />
        </div>
      </Panel>

      <Panel title="備考">
        <Textarea rows={3} showCount maxLength={2000} {...register('note')} />
      </Panel>

      <FormActions>
        {!isNew && (
          <Button variant="danger" icon={<Trash2 size={16} />} onClick={onDelete} loading={del.isPending} style={{ marginRight: 'auto' }}>
            削除
          </Button>
        )}
        <Button onClick={onCancel}>キャンセル</Button>
        <Button type="submit" variant="primary" icon={<Save size={16} />} loading={isSubmitting}>
          {isNew ? '登録する' : '保存する'}
        </Button>
      </FormActions>
    </form>
  );
}
